'use client';

import { cn } from '@core/utils';
import useEmblaCarousel from 'embla-carousel-react';

import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useSelectedSnapIndex } from '@/hooks/useSelectedSnapIndex';

interface DesktopMainBannerSectionProps {
  bannerItems: {
    href: string;
    title: string;
    imageUrl: string;
    target?: string;
  }[];
  className?: string;
}

export const DesktopMainBannerSection = ({
  bannerItems,
  className,
}: DesktopMainBannerSectionProps) => {
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP);

  const isLooping = bannerItems.length > 1;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: isLooping,
    align: 'center',
  });
  const activeIndex = useSelectedSnapIndex(emblaApi);

  const bannerImageWidth = isDesktop
    ? BANNER_WIDTH_DESKTOP
    : BANNER_WIDTH_MOBILE;
  const bannerImageHeight = isDesktop
    ? BANNER_HEIGHT_DESKTOP
    : BANNER_HEIGHT_MOBILE;
  const swiperWidth =
    bannerImageWidth * MIN_BANNER_LENGTH + SWIPER_GAP * (MIN_BANNER_LENGTH - 1);

  //* 배너가 최소 개수 이하일 때 화면을 채우도록 원본을 순환 복제
  const list = (() => {
    if (bannerItems.length === 0 || bannerItems.length > MIN_BANNER_LENGTH) {
      return bannerItems;
    }

    const targetLength = MIN_BANNER_LENGTH * 2;

    return Array.from(
      { length: targetLength },
      (_, index) => bannerItems[index % bannerItems.length],
    );
  })();

  //* 페이지네이션: 복제된 리스트에서의 activeIndex -> 원본 기준 인덱스
  const totalUniqueItems = bannerItems.length;
  const paginationIndex = activeIndex % bannerItems.length;

  if (bannerItems.length < 1) return null;

  return (
    <section
      className={cn('w-fit max-w-full', 'mobile-tablet:w-full', className)}
      key={isDesktop ? 'desktop' : 'mobile'}
    >
      <div className="relative">
        {isLooping && (
          <div
            className="pointer-events-none absolute z-10 hidden mobile-tablet:block"
            style={{
              bottom: 12,
              right: `calc((100% - ${BANNER_WIDTH_MOBILE}px) / 2 + 12px)`,
            }}
          >
            <div className="h-20 w-36 whitespace-nowrap rounded-pill bg-Gray-black/20 font-600 text-Gray-white flex-center typo-caption2">
              {`${paginationIndex + 1} / ${totalUniqueItems}`}
            </div>
          </div>
        )}
        <div
          ref={emblaRef}
          className={cn('max-w-full overflow-hidden')}
          style={{ width: `${swiperWidth}px` }}
        >
          {/* embla가 init 후 잡을 위치를 서버 렌더에 미리 적용 — 없으면 하이드레이션 순간 배너가 좌→중앙으로 점프한다 (init 시 embla가 같은 값으로 덮어씀). loop가 아니면 embla가 첫 스냅을 0으로 트림하므로 이동 자체가 없다 */}
          <div
            className="flex"
            style={
              isLooping
                ? {
                    transform: `translate3d(calc(50% - ${bannerImageWidth / 2}px), 0px, 0px)`,
                  }
                : undefined
            }
          >
            {list.map(({ href, imageUrl, title, target }, index) => (
              <div
                key={`${href}-${index}`}
                className="flex-[0_0_auto]"
                style={{
                  width: bannerImageWidth,
                  minWidth: 0,
                  marginRight: `${SWIPER_GAP}px`,
                }}
              >
                <CustomLink
                  href={href}
                  target={target}
                  aria-label={`${title} 상세페이지로 이동`}
                  className={cn(
                    'rounded-large transition-opacity duration-200 ease-out',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500',
                    'active:opacity-80',
                    'xl:hover:opacity-80',
                    'mobile-tablet:w-full',
                  )}
                >
                  <div
                    className={cn(
                      'relative overflow-hidden rounded-large',
                      'transition-opacity duration-300',
                      activeIndex === index
                        ? 'opacity-100'
                        : 'mobile-tablet:opacity-50',
                    )}
                    style={{
                      width: bannerImageWidth,
                      height: bannerImageHeight,
                    }}
                  >
                    <Image
                      src={imageUrl}
                      fill
                      sizes={`${BREAKPOINTS.MOBILE_TABLET} 335px, 502px`}
                      alt={`${title}에 관한 배너 이미지`}
                      className="rounded-large object-cover"
                      preload={index === 0}
                      fetchPriority={index === 0 ? 'high' : undefined}
                    />
                  </div>
                </CustomLink>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const BANNER_WIDTH_DESKTOP = 502;
const BANNER_WIDTH_MOBILE = 335;
const BANNER_HEIGHT_DESKTOP = 180;
const BANNER_HEIGHT_MOBILE = 120;
const MIN_BANNER_LENGTH = 5;
const SWIPER_GAP = 10;
